/**
 * Participant filter helpers for /rostering/timesheet.
 * Selection is an array of options. "All" is a single sentinel option.
 * API wire: "all" | number[]
 */

export const isAllParticipantsSelection = (selected) => {
  if (!Array.isArray(selected) || selected.length === 0) return true;
  return selected.some((opt) => String(opt?.value) === "all");
};

/** Build default "All …" option for a care-sector label. */
export const buildAllParticipantsOption = (participantLabelPlural = "Participants") => ({
  label: `All ${(participantLabelPlural || "Participants").toLowerCase()}`,
  value: "all",
});

/**
 * Resolve FE selection → API `pt_id` ("all" or int[]).
 * Empty / All → "all".
 */
export const resolveTimesheetPtIdFilter = (selected) => {
  if (isAllParticipantsSelection(selected)) return "all";
  const ids = selected
    .map((opt) => Number(opt?.id ?? opt?.value))
    .filter((id) => Number.isFinite(id) && id > 0);
  if (ids.length === 0) return "all";
  // Stable order for filter-key matching against echoed query_params.
  return [...new Set(ids)].sort((a, b) => a - b);
};

/** Stable key for refetch / memo deps when selection is multi. */
export const participantFilterKey = (selected) => {
  const resolved = resolveTimesheetPtIdFilter(selected);
  if (resolved === "all") return "all";
  return [...resolved].sort((a, b) => a - b).join(",");
};

/**
 * Toggle an option in multi-select.
 * - Choosing "All" clears others.
 * - Choosing a person clears "All".
 * - Deselecting the last person falls back to "All".
 */
export const toggleParticipantSelection = (
  currentSelected,
  option,
  allOption
) => {
  const safeAll = allOption || buildAllParticipantsOption();
  if (!option) return [safeAll];

  if (String(option.value) === "all") {
    return [safeAll];
  }

  const current = Array.isArray(currentSelected) ? currentSelected : [];
  const withoutAll = current.filter((opt) => String(opt?.value) !== "all");
  const optionValue = String(option.value);
  const exists = withoutAll.some((opt) => String(opt?.value) === optionValue);

  const next = exists
    ? withoutAll.filter((opt) => String(opt?.value) !== optionValue)
    : [
        ...withoutAll,
        {
          ...option,
          id: option.id ?? option.value,
          value: option.value,
          label: option.label,
        },
      ];

  return next.length === 0 ? [safeAll] : next;
};

export const isParticipantOptionSelected = (selected, option) => {
  if (!option) return false;
  if (String(option.value) === "all") {
    return isAllParticipantsSelection(selected);
  }
  if (isAllParticipantsSelection(selected)) return false;
  return (selected || []).some(
    (opt) => String(opt?.value) === String(option.value)
  );
};

/**
 * Display order for the timesheet participant dropdown.
 * Selected people are pinned first (most recently selected at the top).
 * Everyone else keeps the incoming order, so "All" stays directly under
 * the pinned group and the alphabetical list is unchanged.
 * "All" / empty selection keeps the original order ("All" is already first).
 * Does not mutate `options` or `selected`.
 */
export const orderTimesheetParticipantOptions = (options, selected) => {
  const list = Array.isArray(options) ? options : [];
  if (isAllParticipantsSelection(selected)) return list;

  const selectedList = (Array.isArray(selected) ? selected : []).filter(
    (opt) => opt && String(opt.value) !== "all"
  );
  if (selectedList.length === 0) return list;

  const byValue = new Map(list.map((opt) => [String(opt?.value), opt]));
  const pinned = [];
  const pinnedKeys = new Set();

  for (let i = selectedList.length - 1; i >= 0; i -= 1) {
    const key = String(selectedList[i]?.value);
    if (pinnedKeys.has(key)) continue;
    const match = byValue.get(key);
    if (!match) continue;
    pinned.push(match);
    pinnedKeys.add(key);
  }

  if (pinned.length === 0) return list;

  const rest = list.filter((opt) => !pinnedKeys.has(String(opt?.value)));
  return [...pinned, ...rest];
};
